import cns from 'classnames'
import { useRef, useState, useCallback } from 'react'
import { useClickAway } from 'react-use'
import { Link } from 'react-router-dom'

import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useWalletDisconnect } from 'modules/wallet/hooks/useWalletDisconnect'
import { useConnectWalletModal } from 'modules/wallet/ui/ConnectWalletModal'
import { Text } from 'shared/ui/common/Text'
import { Button } from 'shared/ui/controls/Button'
import { AddressIcon } from 'modules/blockChain/ui/AddressIcon'
import { trimMiddleString } from 'shared/utils/trimMiddleString'
import { getChainColor, getChainName } from 'modules/blockChain/chains'
import * as links from 'modules/router/links'

import { ReactComponent as WalletSVG } from 'assets/wallet.svg'

import s from './HeaderWallet.module.scss'

type Props = {
  className?: string
}

export function HeaderWallet({ className }: Props) {
  const refButton = useRef<HTMLDivElement | null>(null)
  const refMenu = useRef<HTMLDivElement | null>(null)

  const [isOpened, setOpened] = useState(false)
  const { chainId, isWalletConnected, walletAddress } = useWeb3()
  const connectWalletModal = useConnectWalletModal()
  const disconnect = useWalletDisconnect()

  const handleClose = useCallback(() => setOpened(false), [])
  const handleToggle = useCallback(() => setOpened(v => !v), [])

  const handleDisconnect = useCallback(() => {
    handleClose()
    disconnect()
  }, [handleClose, disconnect])

  const handleClickAway = useCallback(
    (e: MouseEvent) => {
      if (
        e.target === refButton.current ||
        refButton.current?.contains(e.target as HTMLElement)
      ) {
        return
      }
      handleClose()
    },
    [handleClose],
  )

  useClickAway(refMenu, handleClickAway)

  if (!isWalletConnected || !walletAddress) {
    return (
      <div
        className={s.walletButton}
        onClick={() => connectWalletModal.open({})}
        data-test-id="connect-wallet-modal-button"
      >
        <div className={s.network}>
          <Text size={14} weight={500}>
            Connect wallet
          </Text>
        </div>
        <Button isSquare size={40} fashion="glass">
          <WalletSVG />
        </Button>
      </div>
    )
  }

  return (
    <span className={cns(s.wrap, className)}>
      <div ref={refButton} className={s.walletButton} onClick={handleToggle}>
        <div className={s.network}>
          <div
            className={s.networkBulb}
            style={{ backgroundColor: getChainColor(chainId) }}
          />
          <Text size={14} weight={500}>
            {getChainName(chainId)}
          </Text>
        </div>
        <Text size={16} weight={500} className={s.address}>
          {trimMiddleString(String(walletAddress), 4)}
        </Text>
        <AddressIcon address={walletAddress} />
      </div>

      {isOpened && (
        <div ref={refMenu} className={s.menu}>
          <div>
            <Link
              to={links.transactionList}
              className={s.menuButton}
              onClick={handleClose}
            >
              Transaction list
            </Link>
            <div className={s.menuDivider} />
            <div className={s.menuButton} onClick={handleDisconnect}>
              Disconnect
            </div>
          </div>
        </div>
      )}
    </span>
  )
}
